import { IUploadFile } from "../services/document-management.service";
import { SortAttribute } from "../../core/models/sort.model";

export interface OnBaseProgramArea {
    id: number;
    name: string;
}

export interface OnBaseDocumentGroup {
    id: number;
    name: string;
    programAreaId?: number;

}export interface OnBaseMappedDocumentGroup extends OnBaseDocumentGroup{
    programAreaId?: number;
}

export interface OnBaseDocumentType {
    id: number;
    name: string;
    documentGroupName?: string;
}

export interface OnBaseKeyword {
    id: number;
    name: string;
    dataLength: number;
    dataType: string;
}

export interface OnBaseKeywordEntityMapping {
    entityId: string;
    entityFieldPath: string;
}

export interface OnBaseDropdownValue {
	value: string,
	isDefault: boolean
}

export interface OnBaseKeywordSimple extends OnBaseKeyword {
    allowMultiSelect: boolean;
	dropdownValues: OnBaseDropdownValue[];
    mappedEntities: OnBaseKeywordEntityMapping[];
}
export interface OnBaseMappedKeyword extends OnBaseKeywordSimple {
    documentGroupId: number;
    documentGroupName: string
    documentTypeId: number;
    documentTypeName: string
    isRequired: boolean;
}

export interface OnBaseKeywordMappingSearchSpec {
    keywordId?: number;
    keywordName?: string;
    documentGroupId?: number
    documentGroupName?: string
    documentTypeId?: number
    documentTypeName?: string
    isRequired?: boolean;
    entityId?: string;
}

export class OnBaseDocumentTypeSearchSpec {
    id?: number;
    name?: string;
    documentGroupId?: number;
    documentGroupName?: string;

    constructor() { }

    isValid(): boolean {
        return (this.id != null || this.documentGroupId != null || (this.name != null && this.name.length > 0) || (this.documentGroupName != null && this.documentGroupName.length > 0));
    }
}

export class OnBaseKeywordTypeSearchSpec {
    id?: number;
    name?: string;
    documentTypeId?: number;
    documentTypeName?: string;
    documentGroupId?: number;
    documentGroupName?: string;

    constructor() { }

    isValid(): boolean {
        return (this.id != null || (this.name != null && this.name.length > 0)
                || this.documentTypeId != null  || (this.documentTypeName != null && this.documentTypeName.length > 0)
                || this.documentGroupId != null  || (this.documentGroupName != null && this.documentGroupName.length > 0));
    }
}

export interface OnBaseKeywordValue {
    keywordTypeId: number;
    keywordTypeName: string;
    keywordValue: any;
}

export interface CommitProvisionedFileWithKeywordsRequest {
    provisionedFile: IUploadFile,
    documentTypeId: number,
    documentTypeName: string,
    keywordValues: OnBaseKeywordValue[]
}

export interface OnBaseQueryModel {
    documentTypeIds: number[];
    documentTypeNames: string[];
    matchAll?: boolean;
    maxResults: number;
    resultsPerPage: number;
    pageNumber: number;
    displayKeywordTypes: number[];
    fromDate?: Date;
    toDate?: Date;
    searchParameters: OnBaseKeywordQuery[];
    sort: SortAttribute;
    isAscending: boolean;
}

export enum KeywordRelation {
    And = "AND",
    Or = "OR"
}

export class OnBaseKeywordQuery {
    keywordTypeId: number;
    keywordTypeName: string;
    keywordValue: number | string | Date;
    relation: KeywordRelation = KeywordRelation.Or;
    operator: string;

    constructor(id: number, name: string, value: number | string | Date, operator: string) {
        this.keywordTypeId = id;
        this.keywordTypeName = name;
        this.keywordValue = value;
        this.operator = operator;
    }

    toString(): string {
        return `${this.keywordTypeName} ${this.operator} ${this.keywordValue}`;
    }
}

export function keywordEquals(a: OnBaseKeywordQuery, b: OnBaseKeywordQuery): boolean {
    return a.keywordTypeId === b.keywordTypeId && a.keywordValue === b.keywordValue && a.operator === b.operator;
}

export interface DocumentRecord {
    resultNumber: number;
    documentId: number;
    name: string;
    contentType: string;
    displayColumns: KeyValuePair[];
}

export interface QueryResult {
    totalRecords: number;
    records: DocumentRecord[];
}

export interface KeyValuePair {
    key: any;
    value: any;
}

export interface OnBaseDocumentDetails {
    id: number;
    name: string;
    createdBy: string;
    createdDate: Date;
    documentTypeId: number;
    documentTypeName: string;
    fileExtension: string;
    keywords: OnBaseKeywordValue[]
    mimeType: string;
    queueId?: string;
}

export function parseFileNameFromHeaders(headers: string): string {
    let parts = headers.split(';');
    let fileNamePart = parts.find(p => p.trim().toLocaleLowerCase().startsWith("filename"));
    if (fileNamePart == null) return "";
    let nameParts = fileNamePart.split('=');
    if (nameParts == null) return "";
    else if (nameParts.length === 1) return nameParts[0];
    else return nameParts[1].replace(/\"/gi, "");
}

export interface OnBaseUploadedFileLogSearchSpec {
    programArea: string;
    contactId: string;
    documentGroupName: string;
    documentTypeName: string;
    apiNumber: string;
    organizationId: string;
    organizationName: string;
    fromDate: Date;
    toDate: Date;
}

export interface OnBaseFileUploadLog {
    programArea: string;
    contactId: string;
    contactDisplayName: string;
    documentGroupName: string;
    documentTypeName: string;
    apiNumber: string;
    organizationId: string;
    organizationName: string;
    uploadedDocumentName: string;
    uploadTimeStamp: Date;
    onBaseFileUploadLogKeywords: OnBaseKeywordValue[];
    successfullySentToOnbase: boolean;
}

export interface BlobDownloadRequest {
    uri: string;
    contentType: string;
    fileName: string;
}

export interface QueuedDocumentRequest {
    documentId?: number;
    queuedDocumentId?: string;
}

export interface QueuedMessage {
    enqueuedTime: Date;
    content: string;
}

export class FileQueueKeyword  {
    keywordId: number;
    keywordName: string;
    keywordValue: any;
    fileIdentifier: string;
}

export class FileQueueQuery {
    documentTypeName?: string;
    endDate?: Date;
    fileName?: string;
    includePending: boolean = true;
    includeNeedingRequeued: boolean = true;
    includeCompleted: boolean = true;
    startDate?: Date;
}

export interface FileQueueEntry {
    identifier: string;
    programArea: string;
    documentType: string;
    documentGroup: string;
    fileName: string;
    apiNumber: string;
    organizationId: string;
    uploadedById: string;
    uploadedByName: string;
    documentId: string;
    uploadTimeStamp?: Date;
    onBaseDocumentId?: number;
    contentType: string;
    applicationNumber: string;
    keywords: FileQueueKeyword[];
    message?: QueuedMessage;
}